import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type { PortalDomain } from '@breaker/shared-types';

/**
 * Self-serve custom portal domains (0065). One custom host per fleet in
 * v1; the API serves the DNS records to publish alongside the row.
 */

export interface ExpectedDnsRecords {
  txtName: string;
  /** Short zone-relative Name (what most DNS dashboards want). */
  txtNameRelative: string;
  txtValue: string | null;
  cnameName: string;
  cnameNameRelative: string;
  cnameTarget: string | null;
  aTargets: string[];
  /** The DNS zone records are entered into. */
  zone: string;
}

export type PortalDomainWithRecords = PortalDomain & { expected: ExpectedDnsRecords };

/** FAD A1: how the admin host verifies — cname_only when the zone is
 *  already proven by the portal row (one CNAME, no second TXT). */
export type AdminVerificationMode = 'cname_only' | 'full';

export type AdminDomainWithRecords = PortalDomain & {
  expected: ExpectedDnsRecords & { mode: AdminVerificationMode };
};

export interface PortalDomainsView {
  custom: PortalDomainWithRecords | null;
  slugHostname: string | null;
  admin: AdminDomainWithRecords | null;
  /** The admin hostname a claim would create — null until a custom portal
   *  domain exists to derive the zone from. */
  adminSuggestion: string | null;
}

const key = (fleetId: string | null) => ['fleets', fleetId ?? '', 'portal-domains'] as const;

export function usePortalDomains(fleetId: string | null) {
  return useQuery({
    queryKey: key(fleetId),
    enabled: Boolean(fleetId),
    queryFn: () => api<PortalDomainsView>(`/v1/fleets/${fleetId}/portal-domains`),
    staleTime: 15_000,
  });
}

export function useClaimPortalDomain(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { hostname: string; allowApex?: boolean }) =>
      api<{ domain: PortalDomainWithRecords }>(`/v1/fleets/${fleetId}/portal-domains`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(fleetId) }),
  });
}

export function useCheckPortalDomain(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ domain: PortalDomainWithRecords }>(
        `/v1/fleets/${fleetId}/portal-domains/check`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(fleetId) }),
  });
}

export function useClaimAdminDomain(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ domain: AdminDomainWithRecords }>(
        `/v1/fleets/${fleetId}/portal-domains/admin`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(fleetId) }),
  });
}

export function useCheckAdminDomain(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ domain: AdminDomainWithRecords }>(
        `/v1/fleets/${fleetId}/portal-domains/admin/check`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(fleetId) }),
  });
}

export function useRemovePortalDomain(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hostname: string) =>
      api<void>(`/v1/fleets/${fleetId}/portal-domains/${encodeURIComponent(hostname)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(fleetId) }),
  });
}
